/**
 * Voice notes in the browser (FR-2026-0105).
 *
 * MediaRecorder over the microphone, with a live input level read from an
 * AnalyserNode for the recording bar. Stops itself at `maxMs`. The container
 * is picked from what the browser can record AND what the server accepts
 * (the comment voice-note whitelist, sent in the pre-flight config), so a
 * recording is never made in a type the upload would refuse.
 */

export type VoiceStartError = { ok: false; reason: 'unsupported' | 'denied' | 'failed'; message: string };

export interface VoiceResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  /** Input levels sampled while recording (0..1), for the note's waveform. */
  levels: number[];
}

export interface VoiceSession {
  /** Latest input level, 0..1. */
  level(): number;
  elapsedMs(): number;
  /** Stop and resolve `result`. Safe to call twice. */
  stop(): void;
  /** Stop and discard. */
  cancel(): void;
  /** Resolves with the recording, or null when cancelled or empty. */
  result: Promise<VoiceResult | null>;
}

const CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];

/** The first type this browser records that the server also accepts, or null. */
export function pickVoiceMime(accepted: string[]): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
  const ok = new Set(accepted.map((m) => m.toLowerCase()));
  return CANDIDATES.find((m) => ok.has(m.split(';')[0]!) && MediaRecorder.isTypeSupported(m)) ?? null;
}

export function isVoiceSupported(accepted: string[]): boolean {
  return !!navigator.mediaDevices?.getUserMedia && pickVoiceMime(accepted) !== null;
}

export async function startVoice(accepted: string[], maxMs: number): Promise<VoiceSession | VoiceStartError> {
  const mime = isVoiceSupported(accepted) ? pickVoiceMime(accepted) : null;
  if (!mime) {
    return {
      ok: false,
      reason: 'unsupported',
      message: "This browser can't record voice notes here. Type it instead, or attach a file.",
    };
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  } catch (err) {
    const name = (err as DOMException)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return {
        ok: false,
        reason: 'denied',
        message: "Microphone access is blocked. Allow it in your browser's site settings, or type it instead.",
      };
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return { ok: false, reason: 'failed', message: 'No microphone was found. Type it instead, or attach a file.' };
    }
    return { ok: false, reason: 'failed', message: "Couldn't start the microphone. Type it instead, or try again." };
  }

  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 32_000 });
  } catch {
    stream.getTracks().forEach((t) => t.stop());
    return { ok: false, reason: 'unsupported', message: "This browser can't record voice notes here. Type it instead." };
  }

  // Level meter. AudioContext is optional: without it the bar stays flat.
  let ctx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      ctx = new AC();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
    }
  } catch {
    ctx = null;
    analyser = null;
  }
  const buf = analyser ? new Uint8Array(analyser.fftSize) : null;
  let current = 0;
  const levels: number[] = [];
  const sample = setInterval(() => {
    if (!analyser || !buf) return;
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i]! - 128) / 128;
      sum += v * v;
    }
    // RMS of speech sits around 0.02–0.3; lift it into a readable 0..1.
    current = Math.min(1, Math.sqrt(sum / buf.length) * 4);
    levels.push(current);
  }, 80);

  const chunks: Blob[] = [];
  const startedAt = performance.now();
  let stoppedAt: number | null = null;
  let cancelled = false;

  recorder.addEventListener('dataavailable', (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });

  const cleanup = () => {
    clearInterval(sample);
    clearTimeout(cap);
    stream.getTracks().forEach((t) => t.stop());
    if (ctx) void ctx.close().catch(() => undefined);
  };

  const result = new Promise<VoiceResult | null>((resolve) => {
    recorder.addEventListener('stop', () => {
      cleanup();
      const durationMs = (stoppedAt ?? performance.now()) - startedAt;
      if (cancelled || chunks.length === 0) return resolve(null);
      const type = (recorder.mimeType || mime).split(';')[0]!;
      resolve({ blob: new Blob(chunks, { type }), mimeType: type, durationMs: Math.min(durationMs, maxMs), levels });
    });
  });

  const stop = () => {
    if (recorder.state === 'inactive') return;
    stoppedAt = performance.now();
    try { recorder.stop(); } catch { cleanup(); }
  };
  const cap = setTimeout(stop, maxMs);
  recorder.start(1000);

  return {
    level: () => current,
    elapsedMs: () => Math.min(maxMs, (stoppedAt ?? performance.now()) - startedAt),
    stop,
    cancel: () => { cancelled = true; stop(); },
    result,
  };
}

/** Reduce a level history to `n` bars (0..1) for a waveform. */
export function waveform(levels: number[], n: number): number[] {
  if (levels.length === 0) return Array.from({ length: n }, () => 0.15);
  return Array.from({ length: n }, (_, i) => {
    const from = Math.floor((i * levels.length) / n);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / n));
    let peak = 0;
    for (let j = from; j < to && j < levels.length; j++) peak = Math.max(peak, levels[j]!);
    return Math.max(0.12, peak);
  });
}
